import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/website-redesign-services-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/website-redesign-services-colorado")({
  head: () => ({
    meta: [
      { title: "Website Redesign Services: When to Rebuild & What It Costs (2026)" },
      { name: "description", content: "Website redesign services in Colorado: when a redesign beats a rebuild, how to keep your rankings, and honest pricing from $1,850." },
      { property: "og:title", content: "Website Redesign Services: When to Rebuild & What It Costs" },
      { property: "og:description", content: "When a redesign beats a rebuild, how to keep your rankings, and honest Colorado pricing." },
      { property: "og:url", content: `${SITE_URL}/learn/website-redesign-services-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Website redesign services in Colorado",
        description: ARTICLE?.desc ?? "When to redesign, when to rebuild, and what it costs.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/website-redesign-services-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="9 min read"
      title="Website redesign services: when to redesign, when to rebuild, and what it costs"
      intro="Most redesign advice comes from people selling redesigns. Here's the honest version: when a refresh is enough, when you need a full rebuild, how to redesign without losing your Google rankings, and what it should cost in Colorado."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Website redesign services" },
      ]}
      path="/learn/website-redesign-services-colorado"
      faqs={[
        { q: "Will a website redesign hurt my Google rankings?", a: "Not if it's done right. Rankings drop when URLs change without redirects, content gets deleted, or titles get rewritten carelessly. A careful redesign keeps your URLs, content, and rankings intact — and usually improves them." },
        { q: "How much does a website redesign cost in Colorado?", a: "A full redesign of a small business site runs $1,850 to $5,950 depending on page count — same as a new build, because the work is nearly identical. Smaller fixes and updates are $250/hour." },
        { q: "How often should a business website be redesigned?", a: "There's no schedule — redesign when the site stops doing its job: slow on phones, looks dated next to competitors, or visitors aren't calling. For most businesses that's every 4–6 years, not every 2." },
      ]}
    >
      <h2>Redesign or rebuild? The honest test</h2>
      <p>
        A <strong>redesign</strong> keeps your platform and content but updates the look,
        structure, and messaging. A <strong>rebuild</strong> starts over on a modern foundation.
        You probably need a rebuild — not a redesign — if:
      </p>
      <ul>
        <li>Your site takes more than 3 seconds to load on a phone.</li>
        <li>It's built on a page builder or theme nobody dares to touch.</li>
        <li>Simple edits require a developer and a prayer.</li>
        <li>It isn't secure, mobile-friendly, or accessible — and can't be made so cheaply.</li>
      </ul>
      <p>
        If your site is fast and sound but just looks tired, a redesign is enough. Don't let
        anyone sell you a rebuild you don't need.
      </p>

      <h2>How to redesign without losing your rankings</h2>
      <p>
        This is where most redesigns go wrong. The rules are simple but non-negotiable:
      </p>
      <ul>
        <li><strong>Keep your URLs.</strong> If a page ranks, its address stays. If it must change, a permanent redirect points the old address at the new one.</li>
        <li><strong>Keep the content that ranks.</strong> Improve it, don't delete it. Google ranked those words for a reason.</li>
        <li><strong>Keep your titles and headings working.</strong> The phrases that bring traffic stay in place.</li>
        <li><strong>Launch fast, not fancy.</strong> A beautiful site that loads slowly will rank worse than the plain fast one it replaced.</li>
      </ul>

      <h2>What a redesign should cost</h2>
      <p>
        Our published Colorado pricing: <strong>$1,850</strong> for a focused 4-page site,{" "}
        <strong>$2,750</strong> for a 10-page site with foundation SEO done for you, and{" "}
        <strong>$5,950</strong> for up to 50 pages built to rank service-by-city. All include a
        year of hosting, SSL, and a traffic dashboard, and most sites are done in under 7
        business days. Smaller updates and repairs are <strong>$250/hour</strong>. Full details
        on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>What to do next</h2>
      <p>
        The easiest way to decide: request a <Link to="/free-mockup">free website mockup</Link>.
        We'll show you what a modern version of your site would look like — before you spend a
        dollar — and tell you honestly whether you need a redesign or a rebuild. You can also
        see how we handle <Link to="/colorado-web-design">web design across Colorado</Link>.
      </p>
    </ArticleLayout>
  );
}
